import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface Props { children: ReactNode; resetKey?: string; title?: string }
interface State { error: Error | null }

/** Catches render/lazy-load failures so the screen never goes blank; offers retry and reload. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };
  static getDerivedStateFromError(error: Error): State { return { error }; }
  componentDidCatch(error: Error, info: unknown) { console.error("[ErrorBoundary]", error, info); }
  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const chunk = /dynamically imported module|Failed to fetch|Importing a module script|Loading chunk/i.test(error.message);
    return (
      <div role="alert" className="min-h-[50vh] flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-bold">{this.props.title ?? "This page could not be displayed"}</h1>
          <p className="text-muted-foreground">
            {chunk ? "Part of the app failed to load, usually after an update or a network hiccup. Reloading normally fixes it." : "An unexpected error occurred. Try again, or reload the page."}
          </p>
          <p className="text-xs text-muted-foreground break-words font-mono">{error.message}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => this.setState({ error: null })}>Try again</Button>
            <Button variant="outline" onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>
      </div>
    );
  }
}
